import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ObjectType, ObjectStatus, ObjectResponse, CreateObjectRequest } from '@arkitekt/shared';
import { objectsApi } from '@/lib/api';
import { Dialog, DialogHeader, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const OBJECT_TYPES: ObjectType[] = [
  'Application',
  'Technology',
  'BusinessCapability',
  'Person',
  'Organization',
];

const OBJECT_STATUSES: ObjectStatus[] = [
  'draft',
  'active',
  'deprecated',
  'retired',
];

interface ObjectFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spaceId: string;
  object?: ObjectResponse; // If provided, we're editing
  onSuccess?: (object: ObjectResponse) => void;
}

export function ObjectFormModal({
  open,
  onOpenChange,
  spaceId,
  object,
  onSuccess,
}: ObjectFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!object;

  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState<ObjectType>('Application');
  const [status, setStatus] = useState<ObjectStatus>('draft');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when object changes
  useEffect(() => {
    if (object) {
      setName(object.name);
      setType(object.type);
      setStatus(object.status);
      setDescription(object.description || '');
    } else {
      setName('');
      setType('Application');
      setStatus('draft');
      setDescription('');
    }
    setError(null);
  }, [object, open]);

  const createMutation = useMutation({
    mutationFn: (data: CreateObjectRequest) =>
      objectsApi.create(spaceId, data),
    onSuccess: (newObject) => {
      queryClient.invalidateQueries({ queryKey: ['objects', spaceId] });
      onOpenChange(false);
      onSuccess?.(newObject);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Kunne ikke opprette objekt');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; status?: ObjectStatus; description?: string }) =>
      objectsApi.update(spaceId, object!.id, data),
    onSuccess: (updatedObject) => {
      queryClient.invalidateQueries({ queryKey: ['objects', spaceId] });
      queryClient.invalidateQueries({ queryKey: ['object', spaceId, object!.id] });
      onOpenChange(false);
      onSuccess?.(updatedObject);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Kunne ikke oppdatere objekt');
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Navn er påkrevd');
      return;
    }

    if (isEditing) {
      updateMutation.mutate({
        name: name.trim(),
        status,
        description: description.trim() || undefined,
      });
    } else {
      // Create with empty attributes for the chosen type
      const createData: CreateObjectRequest = {
        type,
        name: name.trim(),
        status,
        description: description.trim() || undefined,
        attributes: {},
      } as CreateObjectRequest;
      createMutation.mutate(createData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-md">
      <DialogHeader onClose={() => onOpenChange(false)}>
        {isEditing ? 'Rediger objekt' : 'Opprett nytt objekt'}
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type (only for create) */}
        {!isEditing && (
          <div>
            <label htmlFor="type" className="block text-sm font-medium mb-1">
              Type *
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as ObjectType)}
              className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            >
              {OBJECT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            Navn *
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Navn på objektet..."
            className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            autoFocus
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ObjectStatus)}
            className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
          >
            {OBJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-1">
            Beskrivelse
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Beskrivelse av objektet..."
            rows={3}
            className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none resize-none"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Avbryt
          </Button>
          <Button type="submit" disabled={isPending || !name.trim()}>
            {isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {isEditing ? 'Lagrer...' : 'Oppretter...'}
              </>
            ) : isEditing ? (
              'Lagre endringer'
            ) : (
              'Opprett'
            )}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
